import { isBrowser } from './env';
/**
 * 音频处理
 */
export class AudioManager {
  public static instance: AudioManager;
  private audioContext: AudioContext | null = null;
  private isProcessing: boolean = false;

  private constructor() {}

  static getInstance() {
    if (!AudioManager.instance) {
      AudioManager.instance = new AudioManager();
    }
    return AudioManager.instance;
  }

  getContext() {
    if (!this.audioContext) {
      if (!isBrowser) {
        throw new Error('AudioContext is not supported in node');
      }
      // @ts-ignore
      const AudioAPI = window.AudioContext || window.webkitAudioContext;

      if (!AudioAPI) {
        throw new Error('AudioContext is not supported in this browser');
      }

      this.audioContext = new AudioAPI();
    }
    return this.audioContext;
  }

  static async closeContext() {
    if (!AudioManager.instance) {
      return;
    }
    if (AudioManager.instance.isProcessing) {
      console.warn('AudioContext is still processing, wait before closing');
      return;
    }
    const context = AudioManager.instance?.audioContext;
    if (context) {
      await context.close();
      AudioManager.instance.audioContext = null;
    }
  }

  static async createSilenceAudio(durationMs: number): Promise<Blob> {
    const instance = AudioManager.getInstance();
    const context = instance.getContext();
    const sampleRate = context.sampleRate;
    const silenceBuffer = context.createBuffer(
      2, // 双声道
      Math.ceil((sampleRate * durationMs) / 1000), // 采样率 * 秒数
      sampleRate
    );
    for (let i = 0; i < silenceBuffer.numberOfChannels; i++) {
      const silenceData = silenceBuffer.getChannelData(i);
      // 填充0值，生成静音
      for (let i = 0; i < silenceData.length; i++) {
        silenceData[i] = 0;
      }
    }

    const wavBlob = await AudioManager.audioBufferToWav(silenceBuffer);
    return new Blob([wavBlob], { type: 'audio/wav' });
  }

  /**
   * 将多个音频文件拼接成一个音频
   * @param audioFiles 音频文件数组
   * @returns 拼接后的音频 Blob
   */
  static async concatenateAudios(audioFiles: File[]): Promise<Blob> {
    const instance = AudioManager.getInstance();
    const context = instance.getContext();
    instance.isProcessing = true;

    try {
      // 解码所有音频文件
      const audioBuffers = await Promise.all(
        audioFiles.map(async (file) => {
          const arrayBuffer = await file.arrayBuffer();
          return await context.decodeAudioData(arrayBuffer);
        })
      );

      const totalLength = audioBuffers.reduce((acc, buffer) => acc + buffer.length, 0);

      // 创建新的 AudioBuffer
      const numberOfChannels = Math.max(...audioBuffers.map((buffer) => buffer.numberOfChannels));
      const sampleRate = audioBuffers[0].sampleRate;
      const result = context.createBuffer(numberOfChannels, totalLength, sampleRate);

      // 拼接音频
      let offset = 0;
      for (const buffer of audioBuffers) {
        for (let channel = 0; channel < numberOfChannels; channel++) {
          const resultData = result.getChannelData(channel);
          // 对于超出当前buffer通道数的通道，填充第一个声道
          if (channel < buffer.numberOfChannels) {
            const bufferData = buffer.getChannelData(channel);
            resultData.set(bufferData, offset);
          } else {
            const bufferData = buffer.getChannelData(0);
            resultData.set(bufferData, offset);
          }
        }
        offset += buffer.length;
      }

      // 转换为 Blob
      const wavBlob = await AudioManager.audioBufferToWav(result);
      return new Blob([wavBlob], { type: 'audio/wav' });
    } finally {
      instance.isProcessing = false;
    }
  }

  /**
   * 混合多个音频文件，支持调整每个音频的音量
   * @param audioFiles 音频文件数组
   * @param volumes 对应的音量数组（0-1之间的数值）
   * @returns 混合后的音频 Blob
   */
  static async mixAudios(
    voiceFile: {
      file: File;
      volumn: number;
    },
    bgmFile: {
      file: File;
      volumn: number;
    }
  ): Promise<Blob> {
    const instance = AudioManager.getInstance();
    const context = instance.getContext();
    instance.isProcessing = true;

    try {
      // 解码所有音频文件
      const [voiceBuffer, bgmBuffer] = await Promise.all(
        [voiceFile, bgmFile].map(async ({ file }) => {
          const arrayBuffer = await file.arrayBuffer();
          return await context.decodeAudioData(arrayBuffer);
        })
      );

      // 保证完整播放完 bgm，如果 bgm 长度小于 voice，则循环播放 bgm
      let maxLength = bgmBuffer.length;
      if (maxLength < voiceBuffer.length) {
        maxLength = bgmBuffer.length * Math.ceil(voiceBuffer.length / bgmBuffer.length);
      }

      const numberOfChannels = Math.max(
        ...[voiceBuffer, bgmBuffer].map((buffer) => buffer.numberOfChannels)
      );
      const sampleRate = voiceBuffer.sampleRate;

      // 创建新的 AudioBuffer
      const result = context.createBuffer(numberOfChannels, maxLength, sampleRate);

      // 混合音频
      for (let channel = 0; channel < numberOfChannels; channel++) {
        const resultData = result.getChannelData(channel);

        // 初始化结果数组
        for (let i = 0; i < maxLength; i++) {
          resultData[i] = 0;
        }

        // 叠加每个音频的数据
        [voiceBuffer, bgmBuffer].forEach((buffer, index) => {
          if (channel < buffer.numberOfChannels) {
            const bufferData = buffer.getChannelData(channel);
            const isVoice = index === 0;
            const volume = isVoice ? voiceFile.volumn : bgmFile.volumn;

            for (let i = 0; i < maxLength; i++) {
              const loopedPosition = isVoice ? i : i % buffer.length;
              resultData[i] += (bufferData[loopedPosition] || 0) * volume;
            }
          }
        });

        // 使用动态压缩处理音频过载
        let maxAmplitude = 0;
        // 首先找出最大振幅
        for (let i = 0; i < maxLength; i++) {
          maxAmplitude = Math.max(maxAmplitude, Math.abs(resultData[i]));
        }

        // 如果存在过载，应用软压缩
        if (maxAmplitude > 1) {
          const scalar = 1 / maxAmplitude;
          for (let i = 0; i < maxLength; i++) {
            resultData[i] *= scalar;
          }
        }
      }

      // 转换为 Blob
      const wavBlob = await AudioManager.audioBufferToWav(result);
      return new Blob([wavBlob], { type: 'audio/wav' });
    } finally {
      instance.isProcessing = false;
    }
  }

  /**
   * 将 AudioBuffer 转换为 WAV 格式的 ArrayBuffer
   */
  private static audioBufferToWav(audioBuffer: AudioBuffer): ArrayBuffer {
    const numberOfChannels = audioBuffer.numberOfChannels;
    const sampleRate = audioBuffer.sampleRate;
    const format = 1; // PCM
    const bitDepth = 16;
    const bytesPerSample = bitDepth / 8;
    const blockAlign = numberOfChannels * bytesPerSample;
    const byteRate = sampleRate * blockAlign;
    const dataSize = audioBuffer.length * blockAlign;
    const bufferSize = 44 + dataSize;
    const arrayBuffer = new ArrayBuffer(bufferSize);
    const view = new DataView(arrayBuffer);

    // WAV 文件头
    writeString(view, 0, 'RIFF');
    view.setUint32(4, bufferSize - 8, true);
    writeString(view, 8, 'WAVE');
    writeString(view, 12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, format, true);
    view.setUint16(22, numberOfChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, byteRate, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, bitDepth, true);
    writeString(view, 36, 'data');
    view.setUint32(40, dataSize, true);

    // 写入音频数据
    const offset = 44;
    const channels = [];
    for (let i = 0; i < numberOfChannels; i++) {
      channels.push(audioBuffer.getChannelData(i));
    }

    for (let i = 0; i < audioBuffer.length; i++) {
      for (let channel = 0; channel < numberOfChannels; channel++) {
        const sample = Math.max(-1, Math.min(1, channels[channel][i]));
        const value = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
        view.setInt16(offset + i * blockAlign + channel * bytesPerSample, value, true);
      }
    }

    return arrayBuffer;
  }

  static async getAudioDuration(audioFile: File | Blob): Promise<number> {
    const context = AudioManager.getInstance().getContext();
    if (!context && audioFile instanceof File) {
      return await AudioManager.getAudioDurationByElement(audioFile);
    } else {
      return await AudioManager.getAudioDurationByContext(audioFile);
    }
  }

  /**
   * 检查文件是否为媒体文件,若不支持AudioContext,则默认返回true
   */
  static async checkIsMediaContent(file: File) {
    const instance = AudioManager.getInstance();
    const context = instance.getContext();
    if (context) {
      try {
        const ab = await file.arrayBuffer();
        instance.isProcessing = true;
        await context.decodeAudioData(ab);
        return true;
      } catch (_) {
        return false;
      } finally {
        instance.isProcessing = false;
      }
    }
    return true;
  }

  /**
   * 使用 AudioContext 获取时长
   */
  private static async getAudioDurationByContext(file: File | Blob): Promise<number> {
    const instance = AudioManager.getInstance();
    const context = instance.getContext();
    try {
      const arrayBuffer = await file.arrayBuffer();
      instance.isProcessing = true;
      const audioBuffer = await context.decodeAudioData(arrayBuffer);
      return audioBuffer.duration;
    } finally {
      instance.isProcessing = false;
    }
  }

  /**
   * 使用 audio 元素获取时长
   */
  private static getAudioDurationByElement(file: File): Promise<number> {
    return new Promise((resolve, reject) => {
      const audio = document.createElement('audio');
      const objectUrl = URL.createObjectURL(file);

      const cleanup = () => {
        URL.revokeObjectURL(objectUrl);
        audio.remove();
      };

      const timeoutId = setTimeout(() => {
        cleanup();
        reject(new Error('get audio duration timeout'));
      }, 2000); // 2秒超时

      audio.addEventListener('loadedmetadata', () => {
        clearTimeout(timeoutId);
        const duration = audio.duration;
        cleanup();
        resolve(duration);
      });

      audio.addEventListener('error', (e) => {
        clearTimeout(timeoutId);
        cleanup();
        reject(e);
      });

      audio.preload = 'metadata';
      audio.src = objectUrl;
    });
  }
}

// 辅助函数：将字符串写入 DataView
function writeString(view: DataView, offset: number, string: string) {
  for (let i = 0; i < string.length; i++) {
    view.setUint8(offset + i, string.charCodeAt(i));
  }
}

// 格式化时长, 返回 MM'SS'' 格式，不考虑小时
export function formatDuration(seconds: number) {
  // 四舍五入，确保处理的是整数，与audio元素一致
  seconds = Math.round(seconds);

  // 计算小时、分钟、秒
  const remainingSeconds = seconds % 60;
  const minutes = Math.floor(seconds / 60);
  const remainingMinutes = minutes % 60;

  // 否则返回 MM'SS'' 格式
  return `${remainingMinutes}:${String(remainingSeconds).padStart(2, '0')}`;
}
